/** @format */

import {useEffect, useMemo} from 'react';

import * as R from 'ramda';
import Alert from '@mui/material/Alert';
import {Box, Grid, LinearProgress, Typography} from '@mui/material';
import {Trans} from 'react-i18next';
import {makeStyles} from '@mui/styles';
import {add, format} from 'date-fns';

import {platformsFromAdType} from 'helpers/adTypeHelpers';
import {useCallbackOnReturn, useSearchParams} from 'helpers/routerHelpers';
import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import Card from './Card';
import Header from './Header';
import {
  useCreateSensorExclusionMutation,
  useDeleteSensorExclusionMutation,
  useGetCurrentUserQuery,
} from './graphql/__generated__';
import {SensorCheckType} from './types';
import {groupReportItems} from './helpers';

const useStyles = makeStyles(theme => ({
  list: {
    marginTop: theme.spacing(6),
  },
  noIssuesText: {
    fontSize: 18,
    fontWeight: '400',
    lineHeight: '22px',
  },
}));

const Details = () => {
  const searchParams = useSearchParams();
  const name = searchParams.get('name');

  const {currentAdType, currentCompany} = useCompanyContext();

  const classes = useStyles();

  const {mixpanel} = useMixpanelContext();

  const platforms = platformsFromAdType(currentAdType);

  const now = new Date();
  const startDate = add(now, {days: -29});
  const endDate = now;

  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    skip: !name,
    variables: {
      platforms: platforms.join(','),
      sensorName: name!,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    },
  });

  const [deleteSensorExclusion, {loading: deleteSensorExclusionLoading}] =
    useDeleteSensorExclusionMutation();
  const [createSensorExclusion, {loading: createSensorExclusionLoading}] =
    useCreateSensorExclusionMutation();

  useCallbackOnReturn(refetch);

  const sortedGraphChecks = useMemo(() => {
    if (!data?.currentUser?.currentCompany) return [];

    const {graphSensorChecks} = data.currentUser.currentCompany;

    return R.pipe(
      R.filter<SensorCheckType>(({sensorName}) => sensorName === name),
      R.sortBy(v => v.collectedOn),
    )(graphSensorChecks);
  }, [data, name]);

  useEffect(() => {
    if (data?.currentUser?.currentCompany?.report?.items?.length) {
      mixpanel.track('open_sensor_details', {
        sensor_name: name,
        company_uuid: currentCompany.uuid,
      });
    }
  }, [data, mixpanel, name, currentCompany]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (!name) {
    return <Alert severity='error'>Не указан сенсор</Alert>;
  }
  if (loading || !data?.currentUser?.currentCompany) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {sensorExclusion, report, lastChecks: allLastChecks} = data.currentUser.currentCompany;

  const lastChecks = allLastChecks.filter(v => v.sensor.name === name);

  const {isError} = lastChecks[0].sensor;

  const reportItemGroups = groupReportItems(name, report.items);

  const handleCreateSensorExclusion = async () => {
    if (sensorExclusion) return;

    await createSensorExclusion({variables: {input: {sensorName: name}}});
    refetch();
  };

  const handleDeleteSensorExclusion = async () => {
    if (!sensorExclusion) return;

    await deleteSensorExclusion({variables: {id: sensorExclusion.id}});
    refetch();
  };
  return (
    <>
      <Header
        sensorExclusionLoading={
          deleteSensorExclusionLoading || createSensorExclusionLoading || loading
        }
        sortedGraphChecks={sortedGraphChecks}
        lastChecks={lastChecks}
        reportItems={report.items}
        sensorExclusion={sensorExclusion}
        sensorName={name}
        onCreateSensorExclusion={handleCreateSensorExclusion}
        onDeleteSensorExclusion={handleDeleteSensorExclusion}
      />

      <Grid container className={classes.list} spacing={3}>
        {reportItemGroups.map((vs, i) => (
          <Card key={i} isError={isError} reportItems={vs} sensorName={name} />
        ))}
      </Grid>
      {reportItemGroups.length === 0 && (
        <Box mt={6}>
          <Typography className={classes.noIssuesText}>
            {isError ? (
              <Trans>Сегодня всё в порядке. Так держать!</Trans>
            ) : (
              <Trans>Сегодня нет ни одной рекомендации. Так держать!</Trans>
            )}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default Details;
